import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText, Plus, Download, Upload, Eye, Trash2, AlertTriangle, Calendar } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface Employee {
  id: string;
  employee_code: string;
  first_name: string;
  last_name: string;
}

interface Document {
  id: string;
  employee_id: string;
  document_type: string;
  document_name: string;
  file_url?: string;
  status: string;
  is_required: boolean;
  expiry_date?: string;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
  employee?: Employee;
}

const DOCUMENT_TYPES = [
  'Contrat de travail',
  'Carte d\'identité',
  'Passeport',
  'Permis de travail',
  'Certificat médical',
  'Diplômes et certificats',
  'CV',
  'Photo d\'identité',
  'Extrait de casier judiciaire',
  'Attestation de résidence',
  'Certificat de naissance',
  'Attestation bancaire'
];

const DOCUMENT_STATUS = [
  'En attente',
  'Reçu',
  'Vérifié',
  'Approuvé',
  'Rejeté',
  'Expiré'
];

export function DocumentsManagement() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  const [documentForm, setDocumentForm] = useState({
    employee_id: '',
    document_type: '',
    document_name: '',
    file_url: '',
    status: 'En attente',
    is_required: false,
    expiry_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [documentsResult, employeesResult] = await Promise.all([
        supabase
          .from('employee_documents')
          .select(`
            *,
            employee:employees(id, employee_code, first_name, last_name)
          `)
          .order('created_at', { ascending: false }),
        supabase
          .from('employees')
          .select('id, employee_code, first_name, last_name')
          .eq('status', 'active')
          .order('first_name', { ascending: true })
      ]);

      if (documentsResult.error) throw documentsResult.error;
      if (employeesResult.error) throw employeesResult.error;

      setDocuments(documentsResult.data || []);
      setEmployees(employeesResult.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les documents",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDocumentForm({
      employee_id: '',
      document_type: '',
      document_name: '',
      file_url: '',
      status: 'En attente',
      is_required: false,
      expiry_date: ''
    });
    setEditingDocument(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingDocument) {
        const { error } = await supabase
          .from('employee_documents')
          .update({
            ...documentForm,
            is_required: documentForm.is_required,
            expiry_date: documentForm.expiry_date || null
          })
          .eq('id', editingDocument.id);

        if (error) throw error;

        toast({
          title: "Document modifié",
          description: "Le document a été mis à jour avec succès",
        });
      } else {
        const { error } = await supabase
          .from('employee_documents')
          .insert({
            ...documentForm,
            is_required: documentForm.is_required,
            expiry_date: documentForm.expiry_date || null
          });

        if (error) throw error;

        toast({
          title: "Document ajouté",
          description: "Le document a été créé avec succès",
        });
      }

      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving document:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder le document",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (document: Document) => {
    setEditingDocument(document);
    setDocumentForm({
      employee_id: document.employee_id,
      document_type: document.document_type,
      document_name: document.document_name,
      file_url: document.file_url || '',
      status: document.status,
      is_required: document.is_required,
      expiry_date: document.expiry_date || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;

    try {
      const { error } = await supabase
        .from('employee_documents')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Document supprimé",
        description: "Le document a été supprimé avec succès",
      });

      fetchData();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le document",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: 'default' | 'destructive' | 'outline' | 'secondary' } = {
      'En attente': 'outline',
      'Reçu': 'secondary',
      'Vérifié': 'default',
      'Approuvé': 'default',
      'Rejeté': 'destructive',
      'Expiré': 'destructive'
    };

    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  const isExpiringSoon = (expiryDate: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    return expiry <= thirtyDaysFromNow && expiry > today;
  };

  const isExpired = (expiryDate: string) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) <= new Date();
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesEmployee = selectedEmployee === 'all' || doc.employee_id === selectedEmployee;
    const matchesSearch = searchTerm === '' || 
      doc.document_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.document_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${doc.employee?.first_name} ${doc.employee?.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesEmployee && matchesSearch;
  });

  if (loading) {
    return <div className="flex justify-center items-center h-64">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Gestion des documents RH</h2>
          <p className="text-muted-foreground">
            Gérer les documents des employés, vérifier les expirations et les statuts
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingDocument ? 'Modifier le document' : 'Ajouter un document'}
              </DialogTitle>
              <DialogDescription>
                Remplissez les informations du document employé
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="employee_id">Employé</Label>
                  <Select 
                    value={documentForm.employee_id} 
                    onValueChange={(value) => setDocumentForm(prev => ({ ...prev, employee_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un employé" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((employee) => (
                        <SelectItem key={employee.id} value={employee.id}>
                          {employee.employee_code} - {employee.first_name} {employee.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="document_type">Type de document</Label>
                  <Select 
                    value={documentForm.document_type} 
                    onValueChange={(value) => setDocumentForm(prev => ({ ...prev, document_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner le type" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="document_name">Nom du document</Label>
                <Input
                  id="document_name"
                  value={documentForm.document_name}
                  onChange={(e) => setDocumentForm(prev => ({ ...prev, document_name: e.target.value }))}
                  placeholder="Ex: Contrat de travail - John Doe"
                  required
                />
              </div>

              <div>
                <Label htmlFor="file_url">URL du fichier (optionnel)</Label>
                <Input
                  id="file_url"
                  type="url"
                  value={documentForm.file_url}
                  onChange={(e) => setDocumentForm(prev => ({ ...prev, file_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="status">Statut</Label>
                  <Select 
                    value={documentForm.status} 
                    onValueChange={(value) => setDocumentForm(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_STATUS.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="expiry_date">Date d'expiration (optionnelle)</Label>
                  <Input
                    id="expiry_date"
                    type="date"
                    value={documentForm.expiry_date}
                    onChange={(e) => setDocumentForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="is_required"
                  type="checkbox"
                  checked={documentForm.is_required}
                  onChange={(e) => setDocumentForm(prev => ({ ...prev, is_required: e.target.checked }))}
                  className="rounded border border-gray-300"
                />
                <Label htmlFor="is_required">Document obligatoire</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit">
                  {editingDocument ? 'Modifier' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Rechercher un document, type ou employé..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les employés</SelectItem>
                {employees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {employee.employee_code} - {employee.first_name} {employee.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Documents ({filteredDocuments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employé</TableHead>
                <TableHead>Type de document</TableHead>
                <TableHead>Nom du document</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Date d'expiration</TableHead>
                <TableHead>Obligatoire</TableHead>
                <TableHead>Dernière mise à jour</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocuments.map((document) => (
                <TableRow key={document.id}>
                  <TableCell className="font-medium">
                    {document.employee ? (
                      <div>
                        <div>{document.employee.employee_code}</div>
                        <div className="text-sm text-muted-foreground">
                          {document.employee.first_name} {document.employee.last_name}
                        </div>
                      </div>
                    ) : (
                      'Employé supprimé'
                    )}
                  </TableCell>
                  <TableCell>{document.document_type}</TableCell>
                  <TableCell>{document.document_name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(document.status)}
                      {document.expiry_date && (
                        <>
                          {isExpired(document.expiry_date) && (
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                          )}
                          {isExpiringSoon(document.expiry_date) && !isExpired(document.expiry_date) && (
                            <Calendar className="h-4 w-4 text-orange-500" />
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {document.expiry_date ? (
                      <div className={`${
                        isExpired(document.expiry_date) ? 'text-red-600 font-medium' :
                        isExpiringSoon(document.expiry_date) ? 'text-orange-600 font-medium' :
                        'text-muted-foreground'
                      }`}>
                        {new Date(document.expiry_date).toLocaleDateString('fr-FR')}
                        {isExpired(document.expiry_date) && ' (Expiré)'}
                        {isExpiringSoon(document.expiry_date) && !isExpired(document.expiry_date) && ' (Expire bientôt)'}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Non définie</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {document.is_required ? (
                      <Badge variant="destructive">Obligatoire</Badge>
                    ) : (
                      <Badge variant="outline">Optionnel</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(document.updated_at), { 
                      addSuffix: true, 
                      locale: fr 
                    })}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {document.file_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(document.file_url, '_blank')}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(document)}
                      >
                        <FileText className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(document.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredDocuments.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Aucun document trouvé
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}